import React, { ReactElement, useEffect } from 'react';
import { Box, Typography, DialogContent, Grid2, Divider } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import {
    changePurchaseOrderStatus,
    clearGoodsForPurchaseOrder,
    clearPurchaseOrder,
    importPurchaseOrder,
    setGoodsBatchEntitiesForPurchaseOrder,
} from '../../../redux/actions/purchaseOrderActions';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { SpecializedOrderStatus } from '../../../api/ClientApi';
import { FormatDateText } from '../../../common/datesHelper';
import { DimensionTypeDimensionSR, SpecializedOrderStatusSR } from '../../../common/Types';
import { WarehouseClient } from '../../../api/Extensions';
import { isEmployeeProcurementManager, isEmployeeFinanceManager } from '../../../redux/selectors/employeeSelector';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type PurchaseOrderChangeStatusAlertProps = {
    open: boolean;
    closeForm(): void;
    purchaseOrderId: number;
    purchaseOrderStatus: SpecializedOrderStatus;
};

export default function PurchaseOrderChangeStatusAlert(props: PurchaseOrderChangeStatusAlertProps): ReactElement {
    const { closeForm, open, purchaseOrderId, purchaseOrderStatus } = props;

    const purchaseOrder = useAppSelector((state: RootState) => state.purchaseOrderForm.purchaseOrder);
    const batchArrivalTime = useAppSelector((state: RootState) => state.purchaseOrderForm.batchArrivalTime);
    const saveStatus = useAppSelector((state: RootState) => state.purchaseOrderForm.saveStatus);
    const goodsForPurchaseOrder = useAppSelector(
        (state: RootState) => state.goodsToPurchaseOrderForm.goodBatchEntities,
    );

    const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);

    const isProcurementManager = useAppSelector(isEmployeeProcurementManager);
    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);

    const canSeeValuableInfo = isProcurementManager || isFinanceManager;

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (open) {
            if (!!purchaseOrderId && purchaseOrderId > 0) {
                dispatch(importPurchaseOrder(purchaseOrderId));
            }
        }
    }, [dispatch, purchaseOrderId, open]);

    useEffect(() => {
        if (open) {
            if (!!purchaseOrder && !!purchaseOrderId && purchaseOrderId > 0) {
                WarehouseClient.getAllGoodsForPurchaseOrder(purchaseOrderId).then((res) => {
                    dispatch(
                        setGoodsBatchEntitiesForPurchaseOrder({
                            goods: res,
                            receptionOrders: purchaseOrder.receptionOrders ?? [],
                        }),
                    );
                });
            }
        }
    }, [dispatch, open, purchaseOrder]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleSave = () => {
        dispatch(changePurchaseOrderStatus(purchaseOrderStatus));
    };

    const handleClose = () => {
        dispatch(clearPurchaseOrder());
        dispatch(clearGoodsForPurchaseOrder());
        closeForm();
    };

    return (
        <ResponsiveDialog scroll="body" maxWidth="lg" open={open} onClose={handleClose}>
            <FormDialogTitle>
                Nalog za nabavku - izmena statusa : {SpecializedOrderStatusSR[purchaseOrderStatus]}
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '800px', minWidth: '600px', margin: 'auto' }}>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="h6">Opšte informacije</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} sx={{ mb: 2 }}>
                        <Divider />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1">
                            Dobavljač:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {purchaseOrder?.supplier?.shortName ?? purchaseOrder?.supplier?.name ?? '/'}
                            </span>
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1">
                            Odredište - skladište:{' '}
                            <span style={{ fontWeight: 'bold' }}>{purchaseOrder?.destinationSite?.name ?? '/'}</span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1">
                            Ulazno vreme:{' '}
                            <span style={{ fontWeight: 'bold' }}>
                                {batchArrivalTime ? FormatDateText(batchArrivalTime) : '/'}
                            </span>
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                        <Typography variant="h6">Roba</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Divider />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        {goodsForPurchaseOrder?.map((purchaseOrderGoods) => {
                            return (
                                <Box key={purchaseOrderGoods.index} sx={{ mb: 2 }}>
                                    {canSeeValuableInfo ? (
                                        <Grid2 container spacing={2} sx={{ justifyContent: 'start', width: '100%' }}>
                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <Typography variant="body1">
                                                    {purchaseOrderGoods.goods.article?.title}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 2 }}>
                                                <Typography variant="body1">
                                                    {purchaseOrderGoods.goods.quantity ?? '/'}{' '}
                                                    {purchaseOrderGoods.goods.article?.dimensionType
                                                        ? DimensionTypeDimensionSR[
                                                              purchaseOrderGoods.goods.article.dimensionType
                                                          ]
                                                        : 'kom.'}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 2 }}>
                                                <Typography variant="body1">
                                                    {purchaseOrderGoods.batch.purchasePricePerArticle ?? '/'}{' '}
                                                    {companyDetails?.currency ? companyDetails.currency : ''}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 2 }}>
                                                <Typography variant="body1">
                                                    {purchaseOrderGoods.batch.purchasePricePerArticle &&
                                                    purchaseOrderGoods.goods.quantity
                                                        ? purchaseOrderGoods.batch.purchasePricePerArticle *
                                                          purchaseOrderGoods.goods.quantity
                                                        : '/'}{' '}
                                                    {companyDetails?.currency ? companyDetails.currency : ''}
                                                </Typography>
                                            </Grid2>
                                        </Grid2>
                                    ) : (
                                        <Grid2 container spacing={2} sx={{ justifyContent: 'start', width: '100%' }}>
                                            <Grid2 size={{ xs: 10 }}>
                                                <Typography variant="body1">
                                                    {purchaseOrderGoods.goods.article?.title}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 2 }}>
                                                <Typography variant="body1" textAlign="end">
                                                    {purchaseOrderGoods.goods.quantity ?? '/'}{' '}
                                                    {purchaseOrderGoods.goods.article?.dimensionType
                                                        ? DimensionTypeDimensionSR[
                                                              purchaseOrderGoods.goods.article.dimensionType
                                                          ]
                                                        : 'kom.'}
                                                </Typography>
                                            </Grid2>
                                        </Grid2>
                                    )}
                                </Box>
                            );
                        })}
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
